import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldCheck } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Skeleton, Table, cn, useToast, type Column } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '../../auth/model/auth.store.js';
import { AccessSectionTabs } from './AccessSectionTabs.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { rbacApi, type RoleDto } from '../api/rbac.api.js';
import { PermissionMatrix } from './PermissionMatrix.js';
import { RoleDialog, type RoleFormValues } from './RoleDialog.js';

const ROLES_KEY = ['rbac', 'roles'] as const;

/** Ikki ro'yxat orasidagi farq soni — "N o'zgarish saqlanmadi" uchun. */
const diffCount = (a: string[], b: string[]): number => {
  const left = new Set(a);
  const right = new Set(b);
  let count = 0;
  for (const code of new Set([...a, ...b])) {
    if (left.has(code) !== right.has(code)) count += 1;
  }
  return count;
};

const columns = (selectedId: string | null): Column<RoleDto>[] => [
  {
    key: 'code',
    header: t('rbac.colCode'),
    mono: true,
    render: (role) => (
      <span className={cn(role.id === selectedId ? 'text-accent' : 'text-text')}>{role.code}</span>
    ),
  },
  { key: 'name', header: t('rbac.colName') },
  {
    key: 'permissions',
    header: t('rbac.colPermissions'),
    align: 'right',
    mono: true,
    render: (role) => role.permissions.length,
  },
  {
    key: 'kind',
    header: t('rbac.colKind'),
    render: (role) => (
      <Badge tone={role.isSystem ? 'info' : 'neutral'}>
        {role.isSystem ? t('rbac.kindSystem') : t('rbac.kindCustom')}
      </Badge>
    ),
  },
];

/**
 * Rollar sahifasi: chapda ro'yxat, o'ngda tanlangan rolning ruxsat matritsasi.
 *
 * Tizim rollari (`isSystem`) oddiy administrator uchun faqat o'qish uchun:
 * ular seed bilan keladi va oqim ularga tayanadi — `requisition.approve.org`
 * kimda borligi butun tasdiqlash zanjirini belgilaydi.
 *
 * SUPERADMIN esa ularni o'zgartira oladi. Qulf mutlaq bo'lsa, tizim egasining
 * o'zi ham ruxsatlar to'plamini to'g'rilay olmay qoladi va yagona yo'l bazaga
 * qo'lda kirish bo'lardi — bu esa auditdan tashqarida o'zgartirish. Backend
 * ham xuddi shu qoidani qo'llaydi.
 */
/* Bo'lim marshrutlari `NAV_ITEMS` dan dinamik quriladi, shuning uchun router
   turlari ularning literal nomini bilmaydi — manzil satr sifatida beriladi
   (`AppShell` dagi `go()` ham xuddi shu sababdan). */
const STAFF_PATH: string = '/admin/users';

export const RolesPage = () => {
  const navigate = useNavigate();
  const { canAnywhere } = usePermission();
  const isSuperadmin = useAuthStore((state) => state.user?.isSuperadmin === true);
  const canManage = canAnywhere('rbac.role.manage');
  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ROLES_KEY, queryFn: () => rbacApi.listRoles() });

  const items = useMemo(() => roles.data?.items ?? [], [roles.data]);
  const selected = items.find((role) => role.id === selectedId) ?? null;
  const permissions = draft ?? selected?.permissions ?? [];
  const changed = selected === null ? 0 : diffCount(selected.permissions, permissions);

  const createRole = useMutation({
    mutationFn: (values: RoleFormValues) =>
      rbacApi.createRole({
        code: values.code,
        name: values.name,
        ...(values.description === '' ? {} : { description: values.description }),
      }),
    onSuccess: async (role) => {
      setDialogOpen(false);
      setDialogError(null);
      setSelectedId(role.id);
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      toast.show(t('rbac.roleCreated'), 'success');
    },
    onError: (error: unknown) => {
      setDialogError(error instanceof ApiClientError ? error.message : t('rbac.loadError'));
    },
  });

  const savePermissions = useMutation({
    mutationFn: (input: { id: string; permissions: string[] }) =>
      rbacApi.setRolePermissions(input.id, input.permissions),
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      toast.show(t('rbac.permissionsSaved'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(error instanceof ApiClientError ? error.message : t('rbac.loadError'), 'danger');
    },
  });

  const selectRole = (role: RoleDto): void => {
    setSelectedId(role.id);
    setDraft(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <AccessSectionTabs
        view="roles"
        onChange={(view) => {
          if (view === 'staff') void navigate({ to: STAFF_PATH });
        }}
      />

      <PageHeader
        title={t('rbac.title')}
        description={t('rbac.subtitle')}
        actions={
          canManage ? (
            <Button
              onClick={() => {
                setDialogError(null);
                setDialogOpen(true);
              }}
            >
              <Plus size={16} aria-hidden />
              {t('rbac.newRole')}
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <div className="flex flex-col gap-2">
          {roles.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : roles.isError ? (
            <EmptyState
              className="rounded-[var(--radius-lg)] border border-border bg-surface"
              title={t('rbac.loadError')}
              description={roles.error instanceof ApiClientError ? roles.error.message : undefined}
              action={
                <Button variant="secondary" onClick={() => void roles.refetch()}>
                  {t('rbac.retry')}
                </Button>
              }
            />
          ) : (
            <Table
              caption={t('rbac.rolesCaption')}
              density="compact"
              columns={columns(selectedId)}
              rows={items}
              rowKey={(role) => role.id}
              onRowClick={selectRole}
              empty={t('rbac.rolesEmpty')}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          {selected === null ? (
            <Card>
              <EmptyState
                icon={<ShieldCheck size={20} aria-hidden />}
                title={t('rbac.selectRole')}
                description={t('rbac.selectRoleHint')}
              />
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold tracking-tight text-text">
                  {t('rbac.permissionsTitle')}: {selected.name}
                </h2>
                <span className="tabular text-xs text-text-faint">{selected.code}</span>
                {selected.isSystem ? (
                  <Badge tone="info">
                    {isSuperadmin ? t('rbac.systemRoleEditable') : t('rbac.systemRole')}
                  </Badge>
                ) : null}
              </div>

              <PermissionMatrix
                value={permissions}
                onChange={setDraft}
                readOnly={(selected.isSystem && !isSuperadmin) || !canManage}
              />

              {changed > 0 ? (
                <div
                  className={cn(
                    // Telefonda pastki navigatsiya qatori USTIDA to'xtaydi:
                    // `bottom-3` bo'lsa saqlash tugmalari o'sha qator
                    // ostida qolib, umuman bosilmasdi.
                    'sticky z-[var(--z-sticky-bar)] flex flex-wrap items-center justify-between gap-3',
                    'bottom-[calc(var(--size-tabbar)_+_env(safe-area-inset-bottom)_+_0.5rem)] lg:bottom-3',
                    'rounded-[var(--radius-lg)] border border-border bg-surface px-4 py-3',
                    'shadow-[var(--shadow-2),var(--shadow-rail-info)]',
                  )}
                >
                  <p className="text-sm text-text">{t('rbac.dirty', { count: changed })}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => setDraft(null)}>
                      {t('rbac.cancel')}
                    </Button>
                    <Button
                      loading={savePermissions.isPending}
                      onClick={() =>
                        savePermissions.mutate({ id: selected.id, permissions })
                      }
                    >
                      {t('rbac.save')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <RoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={(values) => createRole.mutate(values)}
        pending={createRole.isPending}
        error={dialogError}
      />
    </div>
  );
};
